export const event_realtime_types = [
  "event_created",
  "event_updated",
  "event_deleted",
  "event_rsvp_updated",
  "event_rsvp_cancelled",
  "event_ticket_purchased",
  "event_published_updated",
  // Backend also emits these (e.g. event_type = `event_${action}`).
  "event_published",
  "event_unpublished",
];

function is_present(value) {
  return value !== null && value !== undefined;
}

function as_object(value) {
  return value && typeof value === "object" ? value : null;
}

export function to_positive_int(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  if (n <= 0) return 0;
  return n;
}

function get_numeric_event_id(raw_data, full_event) {
  const candidate =
    Reflect.get(full_event || {}, "id") ??
    Reflect.get(raw_data || {}, "event_id") ??
    Reflect.get(raw_data || {}, "id");

  return to_positive_int(candidate);
}

function get_full_event_from_payload(payload_data) {
  if (!payload_data || typeof payload_data !== "object") return null;
  if (payload_data.event && typeof payload_data.event === "object") return payload_data.event;
  if (payload_data.event_data && typeof payload_data.event_data === "object") return payload_data.event_data;
  return null;
}

export function normalize_realtime_event_data(data) {
  const raw_data = as_object(data);
  if (!raw_data) return null;

  const full_event = get_full_event_from_payload(raw_data);
  const event_id = get_numeric_event_id(raw_data, full_event);
  if (!event_id) return null;

  const patch = {};
  if (full_event) Object.assign(patch, full_event);
  Reflect.set(patch, "id", event_id);

  // Backend sends "title" but existing UI expects "event_name"
  const copy_map = {
    title: "event_name",
    is_published: "is_published",
    rsvp_status: "rsvp_status",
    user_id: "user_id",
    rsvp_data: "rsvp_data",
    ticket_id: "ticket_id",
    quantity: "quantity",
  };

  for (const [source_key, target_key] of Object.entries(copy_map)) {
    const value = Reflect.get(raw_data, source_key);
    if (!is_present(value)) continue;
    Reflect.set(patch, target_key, value);
  }

  return patch;
}

function build_safe_patch(patch_data) {
  const safe_patch = {};
  for (const [key, value] of Object.entries(patch_data || {})) {
    if (key === "id") continue;
    if (value === null || value === undefined) continue;
    Reflect.set(safe_patch, key, value);
  }
  return safe_patch;
}

export function apply_patch_to_event_object(event_obj, patch_data) {
  if (!event_obj || typeof event_obj !== "object" || !patch_data || typeof patch_data !== "object") {
    return event_obj;
  }
  const safe_patch = build_safe_patch(patch_data);
  return {
    ...event_obj,
    ...safe_patch,
    id: event_obj.id ?? patch_data.id,
  };
}

export function get_event_list(view_model, list_name) {
  const list = Reflect.get(view_model || {}, list_name);
  return Array.isArray(list) ? list : null;
}

function get_safe_patch_or_null(patch_data) {
  if (!patch_data || typeof patch_data !== "object") return null;
  return build_safe_patch(patch_data);
}

function find_event_index_by_id(list, event_id) {
  const id_num = to_positive_int(event_id);
  if (!id_num) return { id_num: 0, index: -1 };
  const index = list.findIndex((item) => Number(item?.id) === id_num);
  return ({ id_num, index });
}

export function update_event_in_list(view_model, list_name, event_id, patch_data) {
  const list = get_event_list(view_model, list_name);
  if (!list) return false;

  const safe_patch = get_safe_patch_or_null(patch_data);
  if (!safe_patch) return false;

  const { id_num, index } = find_event_index_by_id(list, event_id);
  if (index === -1) return false;

  const current = Reflect.get(list, index) || {};
  const next = { ...current, ...safe_patch, id: current.id ?? id_num };
  list.splice(index, 1, next);
  return true;
}

export function remove_event_from_list(view_model, list_name, event_id) {
  const list = Reflect.get(view_model || {}, list_name);
  if (!Array.isArray(list) || !event_id) return false;
  const id_num = Number(event_id);
  const next = list.filter((item) => Number(item?.id) !== id_num);
  if (next.length === list.length) return false;
  Reflect.set(view_model, list_name, next);
  return true;
}

export function patch_event_in_all_lists(view_model, event_id, patch_data, list_names) {
  const names = Array.isArray(list_names)
    ? list_names
    : ["upcoming_events", "my_events", "past_events"];

  let patched = false;
  for (const name of names) {
    patched = update_event_in_list(view_model, name, event_id, patch_data) || patched;
  }

  if (view_model?.selectedEvent && Number(view_model.selectedEvent?.id) === Number(event_id)) {
    view_model.selectedEvent = {
      ...(view_model.selectedEvent || {}),
      ...(build_safe_patch(patch_data) || {}),
      id: view_model.selectedEvent.id
    };
  }

  return patched;
}

export function remove_event_from_all_lists(view_model, event_id, list_names) {
  const names = Array.isArray(list_names)
    ? list_names
    : ["upcoming_events", "my_events", "past_events"];

  for (const name of names) {
    remove_event_from_list(view_model, name, event_id);
  }

  if (view_model?.selectedEvent && Number(view_model.selectedEvent?.id) === Number(event_id)) {
    view_model.selectedEvent = {};
  }
}

function get_rsvp_patch_for_status(status_raw) {
  const status = (status_raw || "").toString().toLowerCase();
  if (!status) return null;

  if (status === "waitlisted") {
    return { is_waitlisted: true, is_rsvped: false };
  }

  if (status === "cancelled" || status === "canceled") {
    return { is_waitlisted: false, is_rsvped: false };
  }

  return { is_waitlisted: false, is_rsvped: true };
}

export function update_event_rsvp_for_current_user(view_model, event_id, rsvp_data, list_names) {
  const user_id = rsvp_data?.user_id;

  const current_user_id = Number(view_model?.user_details?.user_id || 0);
  if (!current_user_id || Number(user_id) !== current_user_id) {
    // Payload does not include RSVP counts/lists, so we can't update other users' RSVP state safely.
    return false;
  }

  const rsvp_patch = get_rsvp_patch_for_status(rsvp_data?.rsvp_status);
  if (!rsvp_patch) return false;
  patch_event_in_all_lists(view_model, event_id, rsvp_patch, list_names);
  return true;
}

function get_rsvp_user_id(rsvp_entry, fallback_user_id) {
  const candidate =
    rsvp_entry?.user_id ??
    rsvp_entry?.user?.id ??
    fallback_user_id;

  const n = Number(candidate);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function normalize_rsvp_status_name(status_raw) {
  return (status_raw || "").toString().trim().toLowerCase();
}

function is_cancelled_rsvp_status(status_raw) {
  const status = normalize_rsvp_status_name(status_raw);
  return status === "cancelled" || status === "canceled";
}

function upsert_rsvp_entry_in_list(rsvp_list, rsvp_entry, user_id_num) {
  const list = Array.isArray(rsvp_list) ? rsvp_list : [];
  if (!user_id_num) return list;

  let found = false;
  const next_list = list.map((item) => {
    if (Number(item?.user_id ?? item?.user?.id) !== user_id_num) return item;
    found = true;
    return {
      ...(item || {}),
      ...(rsvp_entry || {}),
    };
  });

  return found ? next_list : [...list, rsvp_entry];
}

function remove_rsvp_entry_from_list(rsvp_list, user_id_num) {
  const list = Array.isArray(rsvp_list) ? rsvp_list : [];
  if (!user_id_num) return list;
  return list.filter((item) => Number(item?.user_id ?? item?.user?.id) !== user_id_num);
}

function rsvp_status_name(rsvp_entry) {
  return (rsvp_entry?.rsvp_status?.name ?? rsvp_entry?.rsvp_status ?? "").toString().trim().toLowerCase();
}

function sync_rsvp_cap_current_count_from_list(event_obj, next_rsvp_list) {
  const cap = event_obj?.rsvp_cap;
  if (!cap || typeof cap !== "object") return event_obj;

  const list = Array.isArray(next_rsvp_list) ? next_rsvp_list : [];
  const confirmed_count = list.filter((r) => rsvp_status_name(r) === "confirmed").length;
  const current = Number(cap?.rsvp_current_count);

  // Always set it (even if current is NaN), but keep it as a number.
  if (Number.isFinite(current) && current === confirmed_count) return event_obj;

  return ({
    ...(event_obj || {}),
    rsvp_cap: {
      ...(cap || {}),
      rsvp_current_count: confirmed_count,
    },
  });
}

function decrement_rsvp_current_count(event_obj) {
  const cap = event_obj?.rsvp_cap;
  if (!cap || typeof cap !== "object") return event_obj;

  const current = Number(cap?.rsvp_current_count);
  if (!Number.isFinite(current)) return event_obj;
  const next_count = Math.max(0, current - 1);
  if (next_count === current) return event_obj;

  return ({
    ...(event_obj || {}),
    rsvp_cap: {
      ...(cap || {}),
      rsvp_current_count: next_count,
    },
  });
}

function apply_rsvp_cancel_to_event_object(event_obj, event_id, user_id_num) {
  if (!event_obj || typeof event_obj !== "object") return event_obj;
  if (Number(event_obj?.id) !== Number(event_id)) return event_obj;

  const current_rsvp_list = event_obj?.rsvp_list;
  const next_rsvp_list = remove_rsvp_entry_from_list(current_rsvp_list, user_id_num);

  let next_event = event_obj;
  if (next_rsvp_list !== current_rsvp_list) {
    next_event = { ...(next_event || {}), rsvp_list: next_rsvp_list };
  }

  // Keep count consistent with the list; if list is missing, fall back to decrement.
  next_event = sync_rsvp_cap_current_count_from_list(next_event, next_rsvp_list);
  next_event = decrement_rsvp_current_count(next_event);
  return next_event;
}

export function replace_event_in_list(list, event_id, updater) {
  if (!Array.isArray(list)) return false;

  const id_num = to_positive_int(event_id);
  if (!id_num || typeof updater !== "function") return false;

  const idx = list.findIndex((item) => Number(item?.id) === id_num);
  if (idx === -1) return false;

  const current_event = Reflect.get(list, idx) || {};
  const next_event = updater(current_event, id_num);
  if (next_event === current_event) return false;

  list.splice(idx, 1, next_event);
  return true;
}

function update_named_event_holder(view_model, holder_name, event_id, updater) {
  const current_event = Reflect.get(view_model || {}, holder_name);
  if (!current_event || Number(current_event?.id) !== Number(event_id)) return false;

  const next_event = updater(current_event, event_id);
  if (next_event === current_event) return false;

  Reflect.set(view_model, holder_name, next_event);
  return true;
}

export function update_event_holders(view_model, event_id, updater, holder_names = ["selectedEvent", "selected_event", "event"]) {
  let changed = false;

  holder_names.forEach((holder_name) => {
    changed = update_named_event_holder(view_model, holder_name, event_id, updater) || changed;
  });

  return changed;
}

function get_rsvp_update_context(rsvp_update_data) {
  const rsvp_entry = get_rsvp_entry_from_update(rsvp_update_data);
  if (!rsvp_entry) return null;

  const user_id_num = get_rsvp_user_id(rsvp_entry, rsvp_update_data?.user_id);
  if (!user_id_num) return null;

  const status_raw = get_rsvp_status_raw(rsvp_update_data, rsvp_entry);
  return ({
    rsvp_entry,
    user_id_num,
    should_remove: is_cancelled_rsvp_status(status_raw),
  });
}

function get_rsvp_entry_from_update(rsvp_update_data) {
  const raw = rsvp_update_data?.rsvp_data;
  return raw && typeof raw === "object" ? raw : null;
}

function get_rsvp_status_raw(rsvp_update_data, rsvp_entry) {
  return (
    rsvp_update_data?.rsvp_status ??
    rsvp_entry?.rsvp_status?.name ??
    rsvp_entry?.rsvp_status
  );
}

function build_rsvp_list_updater(rsvp_entry, user_id_num, should_remove) {
  return (event_obj) => {
    if (!event_obj || typeof event_obj !== "object") return event_obj;

    const current_rsvp_list = event_obj?.rsvp_list;
    const next_rsvp_list = should_remove
      ? remove_rsvp_entry_from_list(current_rsvp_list, user_id_num)
      : upsert_rsvp_entry_in_list(current_rsvp_list, rsvp_entry, user_id_num);

    if (current_rsvp_list === next_rsvp_list) return event_obj;

    const next_event = { ...(event_obj || {}), rsvp_list: next_rsvp_list };
    return sync_rsvp_cap_current_count_from_list(next_event, next_rsvp_list);
  };
}

export function update_event_rsvp_list_in_all_lists(view_model, event_id, rsvp_update_data, list_names) {
  const ctx = get_rsvp_update_context(rsvp_update_data);
  if (!ctx) return false;

  const id_num = to_positive_int(event_id);
  if (!id_num) return false;

  const names = Array.isArray(list_names) ? list_names : ["upcoming_events", "my_events", "past_events"];
  const updater = build_rsvp_list_updater(ctx.rsvp_entry, ctx.user_id_num, ctx.should_remove);

  let changed = false;

  for (const name of names) {
    const list = get_event_list(view_model, name);
    changed = replace_event_in_list(list, id_num, updater) || changed;
  }

  changed = update_event_holders(view_model, id_num, updater) || changed;
  return changed;
}

export function update_event_rsvp_cancelled_in_all_lists(view_model, event_id, cancel_data, list_names) {
  const user_id_num = to_positive_int(cancel_data?.user_id);
  if (!user_id_num) return false;

  const id_num = to_positive_int(event_id);
  if (!id_num) return false;

  const names = Array.isArray(list_names) ? list_names : ["upcoming_events", "my_events", "past_events"];
  let changed = false;
  const updater = (event_obj, current_event_id) =>
    apply_rsvp_cancel_to_event_object(event_obj, current_event_id, user_id_num);

  for (const name of names) {
    const list = get_event_list(view_model, name);
    changed = replace_event_in_list(list, id_num, updater) || changed;
  }

  changed = update_event_holders(view_model, id_num, updater) || changed;

  return changed;
}
